import useBreakpoints from '@/hooks/useBreakpoints';
import clsx from 'clsx';
import { lazy, PropsWithChildren, Suspense } from 'react';
import { twMerge } from 'tailwind-merge';
import { Button } from 'ui/mobile-ds';
import BottomDrawer from 'ui/retail-ds/BottomDrawer';
const Modal = lazy(() =>
  import('ui/retail-ds/Modal').then((mod) => ({
    default: mod.Modal,
  }))
);

const ModalOverlay = lazy(() =>
  import('ui/retail-ds/Modal').then((mod) => ({
    default: mod.ModalOverlay,
  }))
);

const ModalPortal = lazy(() =>
  import('ui/retail-ds/Modal').then((mod) => ({
    default: mod.ModalPortal,
  }))
);

const ModalContent = lazy(() =>
  import('ui/retail-ds/Modal').then((mod) => ({
    default: mod.ModalContent,
  }))
);

interface IProps {
  isOpen: boolean;
  onOpenChange: (open?: boolean) => void;
  onConfirm: () => void;
  onCancel: () => void;
  title?: string;
  description?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  loading?: boolean;
  dismissAfterConfirm?: boolean;
  titleClassName?: string;
  descriptionClassName?: string;
  confirmButtonClassName?: string;
  cancelButtonClassName?: string;
}

const ConfirmAlert = ({
  isOpen,
  onOpenChange,
  onCancel,
  onConfirm,
  title,
  description,
  cancelButtonText,
  confirmButtonText,
  loading,
  dismissAfterConfirm = true,
  children,
  cancelButtonClassName,
  confirmButtonClassName,
  descriptionClassName,
  titleClassName,
}: PropsWithChildren<IProps>) => {
  const { isMobile } = useBreakpoints();

  const handleCancel = (open?: boolean) => {
    if (open !== true && loading !== true) {
      onCancel();
      onOpenChange();
    }
  };

  const handleConfirm = () => {
    onConfirm();
    if (dismissAfterConfirm) {
      onOpenChange();
    }
  };

  const content = (
    <>
      <h1
        className={twMerge(
          'text-gray-700 font-chromatica text-2xl font-bold leading-[140%]',
          titleClassName
        )}
      >
        {title ?? 'Are you sure?'}
      </h1>
      {description ? (
        <h2
          className={twMerge(
            'text-gray-400 text-base mt-3',
            descriptionClassName
          )}
        >
          {description}
        </h2>
      ) : null}
      {children}
      <div
        className={clsx({
          'flex justify-end mt-8 gap-4': true,
          'flex-col-reverse items-stretch': isMobile,
        })}
      >
        <Button
          disabled={loading}
          intent={'secondary'}
          className={twMerge(
            'text-base w-full md:max-w-[220px] text-mobile-gray-600 border-mobile-gray-400 border',
            cancelButtonClassName
          )}
          onClick={() => handleCancel()}
        >
          {cancelButtonText ?? 'Cancel'}
        </Button>
        <Button
          loading={loading}
          intent={'primary'}
          className={twMerge(
            'text-base w-full md:max-w-[220px]',
            confirmButtonClassName
          )}
          onClick={handleConfirm}
        >
          {confirmButtonText ?? 'Confirm'}
        </Button>
      </div>
    </>
  );

  if (isMobile) {
    return (
      <BottomDrawer
        open={isOpen}
        onOpenChange={handleCancel}
        className="h-min rounded-t-[24px] pb-4"
        mode="full"
      >
        {content}
      </BottomDrawer>
    );
  }

  return (
    <Suspense>
      <Modal open={isOpen} onOpenChange={handleCancel}>
        <ModalPortal>
          <ModalOverlay className="z-50" />
          <ModalContent className="w-full max-w-[480px]">
            {content}
          </ModalContent>
        </ModalPortal>
      </Modal>
    </Suspense>
  );
};

export default ConfirmAlert;
